import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { FormFeedback, FormGroup, Input } from 'reactstrap';
import { FieldLabel } from '@puckeditor/core';

import RadioField from './RadioField';
import ExpirationDateDropdown from '../../../ExpirationDateDropdown';

export const DEFAULT_ACCESS_RESTRICTION = {
  requireName: false,
  requireEmail: false,
  requirePassword: false,
  password: null,
  expiresAt: null,
};

export const normalizeAccessRestriction = (value) => ({
  ...DEFAULT_ACCESS_RESTRICTION,
  ...(value && typeof value === 'object' && !Array.isArray(value) ? value : {}),
});

export const isAccessRestrictionPasswordValid = (value) => {
  const settings = normalizeAccessRestriction(value);
  return !settings.requirePassword
    || (typeof settings.password === 'string' && [...settings.password].length >= 3);
};

const toBool = (v) => v === true || v === 'true';

const AccessRestrictionItemsField = ({ value, onChange }) => {
  const intl = useIntl();
  const settings = normalizeAccessRestriction(value);
  const passwordInvalid = !isAccessRestrictionPasswordValid(settings);

  const update = (patch) => onChange({ ...settings, ...patch });

  const yesNoOptions = [
    { value: true, label: intl.formatMessage({ defaultMessage: 'Yes' }) },
    { value: false, label: intl.formatMessage({ defaultMessage: 'No' }) },
  ];

  return (
    <div className="puck-collapse-field-label">
      <RadioField
        id="puck-access-restriction-name"
        name="puckAccessRestrictionName"
        label={intl.formatMessage({ defaultMessage: 'Require name' })}
        value={settings.requireName}
        onChange={(v) => update({ requireName: toBool(v) })}
        options={yesNoOptions}
        groupStyle={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        }}
      />

      <div className="mt-3">
        <RadioField
          id="puck-access-restriction-email"
          name="puckAccessRestrictionEmail"
          label={intl.formatMessage({ defaultMessage: 'Require email' })}
          value={settings.requireEmail}
          onChange={(v) => update({ requireEmail: toBool(v) })}
          options={yesNoOptions}
          groupStyle={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          }}
        />
      </div>

      <div className="mt-3">
        <RadioField
          id="puck-access-restriction-password"
          name="puckAccessRestrictionPassword"
          label={intl.formatMessage({ defaultMessage: 'Require password' })}
          value={settings.requirePassword}
          onChange={(v) => {
            const enabled = toBool(v);
            update({
              requirePassword: enabled,
              ...(enabled ? {} : { password: '' }),
            });
          }}
          options={yesNoOptions}
          groupStyle={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          }}
        />
      </div>

      {settings.requirePassword && (
        <FormGroup className="mt-3">
          <FieldLabel label={intl.formatMessage({ defaultMessage: 'Password' })} className="mb-0" />
          <Input
            id="puck-access-restriction-password-input"
            size="sm"
            type="text"
            autoComplete="new-password"
            value={settings.password}
            invalid={passwordInvalid}
            placeholder={intl.formatMessage({ defaultMessage: 'Enter page password' })}
            onChange={(e) => update({ password: e.target.value })}
          />
          {passwordInvalid && (
            <FormFeedback>
              {intl.formatMessage({ defaultMessage: 'Password must be at least 3 characters' })}
            </FormFeedback>
          )}
        </FormGroup>
      )}

      <FormGroup className="mt-3 puck-access-restriction-expiration-date-wrapper">
        <FieldLabel label={intl.formatMessage({ defaultMessage: 'Expiration date' })} />
        <ExpirationDateDropdown
          expirationDate={settings.expiresAt ? moment(settings.expiresAt) : null}
          onChange={(date) => update({
            expiresAt: date ? moment(date).toISOString() : null,
          })}
        />
      </FormGroup>
    </div>
  );
};

AccessRestrictionItemsField.propTypes = {
  value: PropTypes.shape({
    requireName: PropTypes.bool,
    requireEmail: PropTypes.bool,
    requirePassword: PropTypes.bool,
    password: PropTypes.string,
    expiresAt: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
};

AccessRestrictionItemsField.defaultProps = {
  value: DEFAULT_ACCESS_RESTRICTION,
};

export default AccessRestrictionItemsField;
